import { shipmentApi } from "#/api/shipmentApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import type { Shipment } from "#/types/shipment";
import { useTableColumns, type ColumnRenderer } from "#/hooks/useTableColumns";
import type { ColumnKey } from "#/config/tableColumnsConfig";
import { getRandomColor } from "#/utils/ColorUtils";
import { Badge, Button, Modal, Table, TextInput, Title } from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconEye, IconSearch, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { logger } from "#/utils/logger";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useTranslation } from "react-i18next";

const ShipmentLogsPage = () => {
  const navigate = useNavigate();
  const { client } = useClient();
  const { canDelete } = usePermission();
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [shipmentToDelete, setShipmentToDelete] = useState<Shipment>();

  // Header labels and visibility come from tableColumnsConfig.shipmentLogs;
  // the cell and the backend sort key live here. Sortable: label, vehicle,
  // location, scanner, date, last updated. Items shows the list's `item_count`.
  const shipmentColumns: Record<ColumnKey<"shipmentLogs">, ColumnRenderer<Shipment>> = {
    id: { cellClassName: "min-w-[100px]", cell: (item) => item.id ?? "--" },
    label: { sortKey: "label", cellClassName: "min-w-[150px]", cell: (item) => item.label ?? "--" },
    vehicle: {
      sortKey: "vehicle",
      cellClassName: "min-w-[180px]",
      cell: (item) =>
        item.vehicle ? (
          <div className="flex flex-col gap-1">
            <span>{item.vehicle.name}</span>
            {item.vehicle.rfid_tag?.rfid && (
              <Badge
                color={getRandomColor(item.vehicle.rfid_tag.rfid)}
                variant="light"
                size="sm"
              >
                {item.vehicle.rfid_tag.rfid}
              </Badge>
            )}
          </div>
        ) : (
          "--"
        ),
    },
    items: {
      cellClassName: "min-w-[100px]",
      cell: (item) => (typeof item.item_count === "number" ? item.item_count : 0),
    },
    location: {
      sortKey: "location",
      cellClassName: "max-w-xs truncate min-w-[200px]",
      cell: (item) => <span title={item.location}>{item.location ?? "--"}</span>,
    },
    scanner: {
      sortKey: "scanner",
      cellClassName: "min-w-[150px]",
      cell: (item) => item.scanner?.name ?? "--",
    },
    date: {
      sortKey: "date",
      cellClassName: "min-w-[120px]",
      cell: (item) => (item.date ? formatDate(item.date, client) : "--"),
    },
    updatedAt: {
      sortKey: "updatedAt",
      cellClassName: "min-w-[180px]",
      cell: (item) =>
        item.updatedAt ? (
          <span>
            {formatDate(item.updatedAt, client)}
            <br />
            {formatTime(item.updatedAt, client)}
          </span>
        ) : (
          "--"
        ),
    },
  };
  const { visible, headers, sortableColumns, sortKeyAt, indexOf } = useTableColumns(
    "shipmentLogs",
    shipmentColumns,
  );
  const [sortBy, setSortBy] = useState<string>("updatedAt");

  const {
    data: { items = [], total = 0 } = {},
    isPending,
    refetch: getShipments,
  } = useQuery({
    queryKey: ["shipments", sortBy, page, limit, sortDirection, debouncedSearch],
    queryFn: async () => {
      try {
        const response = await shipmentApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
        };
      } catch {
        throw new Error("No shipments returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  const deleteShipmentMutation = useMutation({
    mutationFn: async (id: number) => {
      return await shipmentApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("shipments.deleteShipment.success"),
      });
      closeDeleteModal();
      setShipmentToDelete(undefined);
      void getShipments();
    },
    onError: (error) => {
      logger.error("deleteShipment error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("shipments.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          <TextInput
            placeholder={t("shipments.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-80"
          />
          <Pagination
            page={page}
            total={total}
            limit={limit}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={[...headers, t("common.actions")]}
          loading={isPending}
          allowSorting={true}
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            const key = sortKeyAt(e.index);
            if (!key) return;
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(key);
          }}
          defaultSort={
            indexOf("updatedAt") !== undefined
              ? { direction: "DESC", index: indexOf("updatedAt") as number }
              : undefined
          }
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                {visible.map((column) => (
                  <Table.Td key={column.key} className={column.cellClassName}>
                    {column.cell(item)}
                  </Table.Td>
                ))}
                <Table.Td className="min-w-[120px]">
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/fleet/logs/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setShipmentToDelete(item);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        size="lg"
        title={t("shipments.deleteShipment.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col gap-5">
          <div className="flex flex-col gap-3 text-sm">
            <span>{t("shipments.deleteShipment.description")}</span>
            <div className="flex flex-col gap-1 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{shipmentToDelete?.label ?? "--"}</span>
              <span className="text-xs text-gray-600 flex items-center justify-center gap-1">
                {t("shipments.deleteShipment.vehicle")} {shipmentToDelete?.vehicle?.name ?? "--"}
                {shipmentToDelete?.vehicle?.rfid_tag?.rfid && (
                  <Badge
                    color={getRandomColor(shipmentToDelete.vehicle.rfid_tag.rfid)}
                    variant="light"
                    size="sm"
                  >
                    {shipmentToDelete.vehicle.rfid_tag.rfid}
                  </Badge>
                )}
              </span>
              <span className="text-xs text-gray-600">
                {t("shipments.deleteShipment.items")}{" "}
                {typeof shipmentToDelete?.item_count === "number" ? shipmentToDelete.item_count : 0}
              </span>
              <span className="text-xs text-gray-600">
                {t("shipments.deleteShipment.location")} {shipmentToDelete?.location ?? "--"}
              </span>
            </div>
            <span>{t("shipments.deleteShipment.warning")}</span>
            <span className="font-bold">{t("shipments.deleteShipment.confirmation")}</span>
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (shipmentToDelete) {
                    deleteShipmentMutation.mutate(shipmentToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteShipmentMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={!canDelete()}
              >
                {t("shipments.deleteShipment.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShipmentLogsPage;
